import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getActiveHoliday } from "../lib/holidayApi";
import { VG_ORANGE } from "../lib/brand";

// Homepage seasonal module: banner + horizontal design carousel.
// Renders nothing when the program is inactive (admin-controlled).
export function HolidayPromo() {
  const [data, setData] = useState(null);
  const scroller = useRef(null);
  useEffect(() => { getActiveHoliday().then(setData).catch(() => {}); }, []);
  if (!data || !data.program_active || !data.collection) return null;

  const c = data.collection;
  const designs = (c.designs || []).filter((d) => !d.personalization_enabled);
  const scroll = (dir) => scroller.current?.scrollBy({ left: dir * 320, behavior: "smooth" });

  return (
    <section
      data-testid="home-holiday-promo"
      className="bg-neutral-50 border-y border-neutral-200"
    >
      <div className="mx-auto max-w-7xl px-4 py-12">
        <div className="relative overflow-hidden rounded-2xl bg-neutral-900 text-white shadow-lg">
          {c.promo_image && (
            <img src={c.promo_image} alt="" className="absolute inset-0 h-full w-full object-cover" />
          )}
          {/* Light scrim only behind the text (left side); the photo stays bright on the right */}
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-950/90 via-neutral-950/45 to-transparent" />
          <div className="relative px-6 py-12 sm:px-10 md:py-14">
        <p className="flex items-center gap-2 font-heading text-sm font-semibold uppercase tracking-[0.22em] [text-shadow:0_2px_10px_rgba(0,0,0,0.85)]" style={{ color: VG_ORANGE }}>
          <Sparkles className="h-4 w-4" /> Holiday Mat Program
        </p>
        <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold leading-tight [text-shadow:0_3px_16px_rgba(0,0,0,0.9)]">
          {c.name}
        </h2>
        <p className="mt-3 max-w-xl text-base text-neutral-100 [text-shadow:0_2px_12px_rgba(0,0,0,0.9)]">
          Seasonal designs that make a lasting first impression. Choose your design, select a size and
          quantity, and submit your order in minutes.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Button asChild className="h-12 px-7 text-base text-white hover:opacity-90" style={{ backgroundColor: VG_ORANGE }} data-testid="holiday-promo-start">
            <Link to="/holiday-mat-program#build-your-order">Start Your Order <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
          <Button asChild variant="outline" className="h-12 px-7 text-base bg-white/10 text-white border-white/30 hover:bg-white/15 hover:text-white" data-testid="holiday-promo-view">
            <Link to="/holiday-mat-program#designs">View All Designs</Link>
          </Button>
        </div>

        {designs.length > 0 && (
          <div className="relative mt-10">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400">Available designs</p>
              <div className="hidden md:flex gap-2">
                <button onClick={() => scroll(-1)} aria-label="Scroll left" data-testid="holiday-carousel-prev" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 hover:bg-white/10">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button onClick={() => scroll(1)} aria-label="Scroll right" data-testid="holiday-carousel-next" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 hover:bg-white/10">
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div ref={scroller} data-testid="holiday-carousel" className="flex gap-4 overflow-x-auto pb-2 snap-x scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              {designs.map((d) => (
                <Link
                  key={d.id}
                  to="/holiday-mat-program#designs"
                  data-testid={`holiday-carousel-item-${d.id}`}
                  className="group relative w-64 flex-shrink-0 snap-start overflow-hidden rounded-xl border border-white/10 bg-neutral-900"
                >
                  <div className="h-44 overflow-hidden bg-neutral-800">
                    <img src={d.image} alt={d.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  </div>
                  <div className="p-3">
                    <p className="font-heading font-bold text-white group-hover:text-[#ED8B00]">{d.name}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
          </div>
        </div>
      </div>
    </section>
  );
}

// Products page collection card (shown inside the Custom Logos context).
export function HolidayCollectionCard() {
  const [data, setData] = useState(null);
  useEffect(() => { getActiveHoliday().then(setData).catch(() => {}); }, []);
  if (!data || !data.program_active || !data.collection) return null;
  const c = data.collection;
  return (
    <Link
      to="/holiday-mat-program"
      data-testid="products-holiday-card"
      className="group relative mb-6 flex flex-col overflow-hidden rounded-xl border-2 shadow-sm transition-all hover:shadow-md sm:flex-row"
      style={{ borderColor: VG_ORANGE }}
    >
      <div className="relative h-48 w-full overflow-hidden bg-neutral-900 sm:h-auto sm:w-72">
        <img src={c.hero_image} alt={c.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <span className="absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white" style={{ backgroundColor: VG_ORANGE }}>
          Seasonal Program
        </span>
      </div>
      <div className="flex flex-1 flex-col justify-center p-6">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider" style={{ color: VG_ORANGE }}>
          <Sparkles className="h-3.5 w-3.5" /> Holiday Mat Program
        </p>
        <h3 className="mt-1.5 font-heading text-2xl font-extrabold text-neutral-900 group-hover:text-[#ED8B00]">
          {c.name}
        </h3>
        <p className="mt-2 text-sm text-neutral-600">
          Seasonal designs to welcome visitors with style. Multiple sizes available · Spectra Print H.D. · Custom Printed Mats.
        </p>
        <span className="mt-4 inline-flex items-center text-sm font-semibold text-[#ED8B00]">
          Start your order <ArrowRight className="ml-1 h-4 w-4" />
        </span>
      </div>
    </Link>
  );
}
